import { useState, useEffect } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  examReportsData,
  getPatientById,
  getDoctorById,
  getPatientHistory,
} from "@/data/mockData";
import { pdtaData, describeFrequency } from "@/data/adminConfigData";
import { servicesData as allServicesData, serviceBundlesData as allBundlesData } from "@/data/mockData";
import {
  ArrowLeft,
  User,
  Calendar,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Send,
  PenTool,
  History,
  Activity,
  Stethoscope,
  Shield,
  Lock,
  Fingerprint,
  Save,
  Sparkles,
  Heart,
  Watch,
  Footprints,
  Moon,
  Flame,
  Wind,
  CalendarPlus
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";
import { ECGMonitor } from "@/components/medical/ECGWaveform";
import { SpirometryMonitor } from "@/components/medical/SpirometryChart";
import { getAllHealthData } from "@/services/huaweiHealth";

// Reference ranges for results
const referenceRanges = {
  wbc: { min: 4, max: 11, unit: "K/µL", name: "Globuli Bianchi" },
  rbc: { min: 4.5, max: 5.5, unit: "M/µL", name: "Globuli Rossi" },
  hgb: { min: 12, max: 17, unit: "g/dL", name: "Emoglobina" },
  hct: { min: 36, max: 50, unit: "%", name: "Ematocrito" },
  plt: { min: 150, max: 400, unit: "K/µL", name: "Piastrine" },
  mcv: { min: 80, max: 100, unit: "fL", name: "MCV" },
  mch: { min: 27, max: 33, unit: "pg", name: "MCH" },
  mchc: { min: 32, max: 36, unit: "g/dL", name: "MCHC" },
  heartRate: { min: 60, max: 100, unit: "bpm", name: "Frequenza Cardiaca" },
  prInterval: { min: 120, max: 200, unit: "ms", name: "Intervallo PR" },
  qrsDuration: { min: 60, max: 120, unit: "ms", name: "Durata QRS" },
  qtInterval: { min: 350, max: 450, unit: "ms", name: "Intervallo QT" },
  totalCholesterol: { min: 0, max: 200, unit: "mg/dL", name: "Colesterolo Totale" },
  hdl: { min: 40, max: 200, unit: "mg/dL", name: "Colesterolo HDL" },
  ldl: { min: 0, max: 100, unit: "mg/dL", name: "Colesterolo LDL" },
  triglycerides: { min: 0, max: 150, unit: "mg/dL", name: "Trigliceridi" },
  fev1: { min: 2.5, max: 5, unit: "L", name: "FEV1" },
  fvc: { min: 3, max: 6, unit: "L", name: "FVC" },
  fev1Fvc: { min: 70, max: 100, unit: "%", name: "FEV1/FVC" },
  pef: { min: 5, max: 12, unit: "L/s", name: "PEF" },
  hba1c: { min: 4, max: 7, unit: "%", name: "HbA1c" },
  estimatedAvgGlucose: { min: 70, max: 150, unit: "mg/dL", name: "Glicemia Media" }
};

const getValueStatus = (key, value) => {
  const range = referenceRanges[key];
  if (!range || typeof value === 'string') return 'normal';
  if (value < range.min) return 'low';
  if (value > range.max) return 'high';
  return 'normal';
};

const ResultItem = ({ keyName, value }) => {
  const range = referenceRanges[keyName];
  if (!range) return null;
  
  const status = getValueStatus(keyName, value);
  
  return (
    <div className={cn(
      "flex items-center justify-between p-3 rounded-lg border",
      status === 'high' && "bg-destructive/5 border-destructive/30",
      status === 'low' && "bg-warning/5 border-warning/30",
      status === 'normal' && "bg-success/5 border-success/30"
    )}>
      <div>
        <p className="font-medium">{range.name}</p>
        <p className="text-xs text-muted-foreground">
          Rif: {range.min} - {range.max} {range.unit}
        </p>
      </div>
      <div className="text-right">
        <p className={cn(
          "text-lg font-bold",
          status === 'high' && "text-destructive",
          status === 'low' && "text-warning",
          status === 'normal' && "text-success"
        )}>
          {typeof value === 'string' ? value : value.toFixed(1)}
        </p>
        <p className="text-xs text-muted-foreground">{range.unit}</p>
      </div>
    </div>
  );
};

export const DoctorReportDetailPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { currentDoctor } = useOutletContext();
  
  // Initialize data directly from mock data
  const initialReport = examReportsData.find(r => r.id === reportId);
  const initialPatient = initialReport ? getPatientById(initialReport.patientId) : null;
  const initialHistory = initialPatient ? getPatientHistory(initialPatient.id) : [];
  
  const [report, setReport] = useState(initialReport);
  const [patient] = useState(initialPatient);
  const [patientHistory] = useState(initialHistory);
  const [reportText, setReportText] = useState(initialReport?.report || "");
  const [isSaving, setIsSaving] = useState(false);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [signaturePin, setSignaturePin] = useState("");
  const [isSigning, setIsSigning] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [wearableData, setWearableData] = useState(null);
  const [loadingWearable, setLoadingWearable] = useState(true);
  const [pdtaDialogOpen, setPdtaDialogOpen] = useState(false);
  const [selectedPdtaId, setSelectedPdtaId] = useState(null);

  // Load wearable data on mount
  useEffect(() => {
    const loadWearableData = async () => {
      try {
        setLoadingWearable(true);
        const data = await getAllHealthData();
        setWearableData(data);
      } catch (error) {
        console.error('Error loading wearable data:', error);
      } finally {
        setLoadingWearable(false);
      }
    };
    loadWearableData();
  }, []);

  // Generate AI report based on exam results
  const generateAIReport = async () => {
    setIsGeneratingAI(true);
    await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate AI processing
    
    // Build report text based on exam results
    const results = report.rawResults;
    let generatedText = "";
    
    // Analyze results and generate appropriate text
    const abnormalValues = [];
    const normalValues = [];
    
    Object.entries(results).forEach(([key, value]) => {
      const range = referenceRanges[key];
      if (range && typeof value === 'number') {
        if (value < range.min || value > range.max) {
          abnormalValues.push({ name: range.name, value, unit: range.unit, status: value < range.min ? 'basso' : 'elevato', range: `${range.min}-${range.max}` });
        } else {
          normalValues.push({ name: range.name, value, unit: range.unit });
        }
      }
    });
    
    // Generate text based on service type
    if (report.serviceId === 'ecg') {
      generatedText = `Esame elettrocardiografico eseguito in data odierna.\n\n`;
      if (results.rhythm) generatedText += `Ritmo: ${results.rhythm}. `;
      if (results.heartRate) generatedText += `Frequenza cardiaca: ${results.heartRate} bpm. `;
      generatedText += `\n\nIntervalli: `;
      if (results.prInterval) generatedText += `PR ${results.prInterval}ms, `;
      if (results.qrsDuration) generatedText += `QRS ${results.qrsDuration}ms, `;
      if (results.qtInterval) generatedText += `QT ${results.qtInterval}ms.`;
    } else if (report.serviceId === 'emocromo') {
      generatedText = `Esame emocromocitometrico completo.\n\n`;
      generatedText += `Parametri analizzati: globuli bianchi, globuli rossi, emoglobina, ematocrito, piastrine, indici eritrocitari.\n\n`;
    } else if (report.serviceId === 'colesterolo') {
      generatedText = `Profilo lipidico completo.\n\n`;
      if (results.totalCholesterol) generatedText += `Colesterolo totale: ${results.totalCholesterol} mg/dL. `;
      if (results.hdl) generatedText += `HDL: ${results.hdl} mg/dL. `;
      if (results.ldl) generatedText += `LDL: ${results.ldl} mg/dL. `;
      if (results.triglycerides) generatedText += `Trigliceridi: ${results.triglycerides} mg/dL.\n\n`;
    } else if (report.serviceId === 'spirometria') {
      generatedText = `Esame spirometrico.\n\n`;
      if (results.fev1) generatedText += `FEV1: ${results.fev1} L. `;
      if (results.fvc) generatedText += `FVC: ${results.fvc} L. `;
      if (results.fev1Fvc) generatedText += `Indice di Tiffeneau (FEV1/FVC): ${results.fev1Fvc}%. `;
      if (results.pef) generatedText += `PEF: ${results.pef} L/s.\n\n`;
    } else if (report.serviceId === 'emoglobina_glicata') {
      generatedText = `Dosaggio emoglobina glicata (HbA1c).\n\n`;
      if (results.hba1c) generatedText += `HbA1c: ${results.hba1c}%. `;
      if (results.estimatedAvgGlucose) generatedText += `Glicemia media stimata: ${results.estimatedAvgGlucose} mg/dL.\n\n`;
    } else {
      generatedText = `Esame ${report.serviceName} eseguito in data odierna.\n\n`;
    }
    
    // Add analysis
    if (abnormalValues.length > 0) {
      generatedText += `\nValori alterati:\n`;
      abnormalValues.forEach(v => {
        generatedText += `- ${v.name}: ${v.value} ${v.unit} (${v.status}, rif. ${v.range})\n`;
      });
      generatedText += `\nSi consiglia follow-up clinico per valutazione dei parametri alterati.`;
    } else {
      generatedText += `\nTutti i parametri rientrano nei range di riferimento.\nEsame nella norma.`;
    }
    
    setReportText(generatedText);
    setIsGeneratingAI(false);
    toast.success("Bozza referto generata con AI");
  };

  if (!report || !patient) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">Referto non trovato</p>
          <Button variant="link" onClick={() => navigate("/doctor/pending")}>
            Torna alla lista
          </Button>
        </div>
      </div>
    );
  }

  const handleSaveReport = async () => {
    setIsSaving(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    // Mock save - in real app would call API
    // Update the original data in mockData (for demo purposes)
    const originalReport = examReportsData.find(r => r.id === reportId);
    if (originalReport) {
      originalReport.report = reportText;
      originalReport.status = 'in_review';
    }
    setReport({ ...report, report: reportText, status: 'in_review' });
    setIsSaving(false);
    toast.success("Referto salvato con successo");
  };

  const handleSignReport = async () => {
    if (signaturePin.length < 4) {
      toast.error("Inserisci un PIN valido");
      return;
    }
    
    setIsSigning(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Mock signature - update original data
    const originalReport = examReportsData.find(r => r.id === reportId);
    const newSignature = {
      doctorId: currentDoctor.id,
      signedAt: new Date().toISOString(),
      signatureId: `SIG-${new Date().toISOString().slice(0,10).replace(/-/g,'')}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
      verified: true
    };
    
    if (originalReport) {
      originalReport.report = reportText;
      originalReport.status = 'signed';
      originalReport.signature = newSignature;
    }
    
    setIsSigning(false);
    setShowSignDialog(false);
    toast.success("Referto firmato digitalmente!");
    navigate("/doctor/pending");
  };

  const canSign = reportText.trim().length > 20;
  const isReadOnly = report.status === 'signed' || report.status === 'sent';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          data-testid="back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-bold">Referto Esame</h1>
          <p className="text-muted-foreground">{report.serviceName}</p>
        </div>
        <div className="flex items-center gap-2">
          {!isReadOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/booking?from=specialist&patientId=${report.patientId}&pharmacyId=ph1&notes=${encodeURIComponent("Approfondimento richiesto da " + (report.serviceName || "esame precedente"))}`)}
              className="gap-2"
              data-testid="specialist-book-followup-btn"
            >
              <CalendarPlus className="h-4 w-4" />
              <span className="hidden sm:inline">Prenota approfondimento</span>
            </Button>
          )}
          {!isReadOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPdtaDialogOpen(true)}
              className="gap-2"
              data-testid="specialist-activate-pdta-btn"
            >
              <Sparkles className="h-4 w-4" />
              <span className="hidden sm:inline">Attiva PDTA</span>
            </Button>
          )}
          {report.priority === 'urgent' && (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="h-3 w-3" />
              Urgente
            </Badge>
          )}
          {report.status === 'pending' && (
            <Badge variant="secondary">In attesa</Badge>
          )}
          {report.status === 'in_review' && (
            <Badge className="bg-info/10 text-info border-info/20">In revisione</Badge>
          )}
          {report.status === 'signed' && (
            <Badge className="bg-success/10 text-success border-success/20 gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Firmato
            </Badge>
          )}
          {report.status === 'sent' && (
            <Badge className="bg-primary/10 text-primary border-primary/20 gap-1">
              <Send className="h-3 w-3" />
              Inviato
            </Badge>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content - Left 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Patient Info Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-5 w-5 text-primary" />
                Dati Paziente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">Nome</p>
                  <p className="font-semibold">{patient.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Codice Fiscale</p>
                  <p className="font-mono text-sm">{patient.fiscalCode}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Data di Nascita</p>
                  <p>{format(new Date(patient.dateOfBirth), "d MMMM yyyy", { locale: it })} ({patient.age} anni)</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Sesso</p>
                  <p>{patient.gender === 'M' ? 'Maschio' : 'Femmina'}</p>
                </div>
              </div>
              
              {/* Medical Information Section */}
              <div className="mt-4 pt-4 border-t space-y-3">
                <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Stethoscope className="h-4 w-4" />
                  Informazioni Mediche
                </p>
                
                {/* Chronic Conditions */}
                <div className="p-3 rounded-lg bg-warning/5 border border-warning/20">
                  <p className="text-xs font-medium text-warning mb-2">Patologie Croniche</p>
                  {patient.chronicConditions && patient.chronicConditions.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {patient.chronicConditions.map((cond, idx) => (
                        <Badge key={idx} variant="outline" className="bg-warning/10 text-warning border-warning/30">
                          {cond}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">Nessuna patologia cronica nota</p>
                  )}
                </div>
                
                {/* Allergies */}
                <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/20">
                  <p className="text-xs font-medium text-destructive mb-2">Allergie</p>
                  {patient.allergies && patient.allergies.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {patient.allergies.map((allergy, idx) => (
                        <Badge key={idx} variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">
                          {allergy}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">Nessuna allergia nota</p>
                  )}
                </div>
                
                {/* Blood Type if available */}
                {patient.bloodType && (
                  <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                    <p className="text-xs font-medium text-primary mb-1">Gruppo Sanguigno</p>
                    <p className="text-lg font-bold text-primary">{patient.bloodType}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Exam Results Card */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Activity className="h-5 w-5 text-primary" />
                  Risultati Esame
                </CardTitle>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  {format(new Date(report.examDate), "d MMMM yyyy 'alle' HH:mm", { locale: it })}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                {Object.entries(report.rawResults).map(([key, value]) => (
                  <ResultItem key={key} keyName={key} value={value} />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* ECG Waveform Card - Only for ECG exams */}
          {report.serviceId === 'ecg' && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Heart className="h-5 w-5 text-destructive" />
                  Tracciato ECG
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ECGMonitor
                  heartRate={report.rawResults.heartRate || 72}
                  rhythm={report.rawResults.rhythm || "Sinusale"}
                  prInterval={report.rawResults.prInterval || 160}
                  qrsDuration={report.rawResults.qrsDuration || 90}
                  qtInterval={report.rawResults.qtInterval || 400}
                />
              </CardContent>
            </Card>
          )}

          {/* Spirometry Chart - Only for spirometry exams */}
          {report.serviceId === 'spirometria' && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Wind className="h-5 w-5 text-info" />
                  Grafico Spirometria
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SpirometryMonitor
                  fev1={report.rawResults.fev1 || 3.5}
                  fvc={report.rawResults.fvc || 4.5}
                  fev1Fvc={report.rawResults.fev1Fvc || 78}
                  pef={report.rawResults.pef || 9.5}
                />
              </CardContent>
            </Card>
          )}

          {/* Wearable Data Card */}
          {wearableData && !loadingWearable && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Watch className="h-5 w-5 text-primary" />
                    Dati Dispositivi Indossabili
                  </CardTitle>
                  {wearableData.isDemo && (
                    <Badge variant="outline" className="text-xs">Dati Demo</Badge>
                  )}
                </div>
                <CardDescription>
                  Ultimi dati dal dispositivo {wearableData.device?.device?.name || 'connesso'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {/* Steps */}
                  <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Footprints className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-muted-foreground">Passi Oggi</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">
                      {wearableData.today?.steps?.toLocaleString() || '-'}
                    </p>
                  </div>
                  
                  {/* Heart Rate */}
                  <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className="h-4 w-4 text-red-600" />
                      <span className="text-sm text-muted-foreground">Freq. Cardiaca</span>
                    </div>
                    <p className="text-2xl font-bold text-red-600">
                      {wearableData.today?.heartRate?.current || '-'} <span className="text-sm font-normal">bpm</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Riposo: {wearableData.today?.heartRate?.resting || '-'} bpm
                    </p>
                  </div>
                  
                  {/* Sleep */}
                  <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Moon className="h-4 w-4 text-purple-600" />
                      <span className="text-sm text-muted-foreground">Sonno</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-600">
                      {wearableData.today?.sleep?.duration?.toFixed(1) || '-'} <span className="text-sm font-normal">ore</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Qualità: {wearableData.today?.sleep?.quality || '-'}%
                    </p>
                  </div>
                  
                  {/* SpO2 */}
                  <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-muted-foreground">SpO2</span>
                    </div>
                    <p className="text-2xl font-bold text-green-600">
                      {wearableData.today?.spo2 || '-'}<span className="text-sm font-normal">%</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Stress: {wearableData.today?.stress || '-'}/100
                    </p>
                  </div>
                </div>
                
                {/* Weekly Trends */}
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium mb-3">Trend Settimanale Passi</p>
                  <div className="flex items-end gap-1 h-16">
                    {wearableData.trends?.steps?.map((day, idx) => (
                      <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full bg-primary/80 rounded-t"
                          style={{ height: `${(day.value / 12000) * 60}px` }}
                        />
                        <span className="text-xs text-muted-foreground">{day.date.split(' ')[0]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Report Writing Card */}
          <Card className={cn(isReadOnly && "opacity-75")}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="h-5 w-5 text-primary" />
                    Referto Medico
                  </CardTitle>
                  <CardDescription>
                    {isReadOnly 
                      ? "Questo referto è stato già firmato e non può essere modificato"
                      : "Scrivi il referto basandoti sui risultati dell'esame"
                    }
                  </CardDescription>
                </div>
                {!isReadOnly && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 border-primary/30 hover:bg-primary/5"
                    onClick={generateAIReport}
                    disabled={isGeneratingAI}
                    data-testid="generate-ai-report-btn"
                  >
                    {isGeneratingAI ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    ) : (
                      <Sparkles className="h-4 w-4 text-primary" />
                    )}
                    Genera con AI
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Inserisci il testo del referto medico..."
                className="min-h-[200px] resize-none"
                value={reportText}
                onChange={(e) => setReportText(e.target.value)}
                disabled={isReadOnly || isGeneratingAI}
                data-testid="report-textarea"
              />
              
              {!isReadOnly && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={handleSaveReport}
                    disabled={isSaving || reportText.trim().length === 0}
                    data-testid="save-report-btn"
                  >
                    {isSaving ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Salva Bozza
                  </Button>
                  <Button
                    className="gap-2 flex-1"
                    onClick={() => setShowSignDialog(true)}
                    disabled={!canSign}
                    data-testid="sign-report-btn"
                  >
                    <PenTool className="h-4 w-4" />
                    Firma Digitale
                  </Button>
                </div>
              )}

              {/* Signature Info */}
              {report.signature && (
                <div className="p-4 rounded-lg bg-success/5 border border-success/30">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-success/10 flex items-center justify-center">
                      <Shield className="h-5 w-5 text-success" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-success">Referto Firmato Digitalmente</p>
                      <p className="text-sm text-muted-foreground">
                        Firmato da {getDoctorById(report.signature.doctorId)?.name || 'Medico'} il{' '}
                        {format(new Date(report.signature.signedAt), "d MMMM yyyy 'alle' HH:mm", { locale: it })}
                      </p>
                      <p className="text-xs font-mono text-muted-foreground mt-1">
                        ID Firma: {report.signature.signatureId}
                      </p>
                    </div>
                    <CheckCircle2 className="h-6 w-6 text-success" />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Patient History */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <History className="h-5 w-5 text-primary" />
                Storico Paziente
              </CardTitle>
              <CardDescription>
                Esami precedenti per confronto
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                <div className="px-6 pb-6 space-y-4">
                  {patientHistory.length === 0 ? (
                    <div className="text-center py-8">
                      <History className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                      <p className="text-muted-foreground">Nessun esame precedente</p>
                    </div>
                  ) : (
                    patientHistory.map((historyItem, idx) => (
                      <div
                        key={historyItem.id}
                        className={cn(
                          "p-4 rounded-lg border transition-all",
                          idx === 0 && "border-primary/30 bg-primary/5"
                        )}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <Badge variant={idx === 0 ? "default" : "outline"}>
                            {idx === 0 ? "Più recente" : format(new Date(historyItem.date), "dd/MM/yyyy")}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {historyItem.services.join(", ")}
                          </span>
                        </div>
                        <div className="space-y-2">
                          {historyItem.results.slice(0, 4).map((result, ridx) => (
                            <div key={ridx} className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">{result.name}</span>
                              <span className={cn(
                                "font-medium",
                                result.status === 'high' && "text-destructive",
                                result.status === 'low' && "text-warning",
                                result.status === 'normal' && "text-success"
                              )}>
                                {result.value} {result.unit}
                              </span>
                            </div>
                          ))}
                          {historyItem.results.length > 4 && (
                            <p className="text-xs text-muted-foreground text-center pt-2">
                              +{historyItem.results.length - 4} altri parametri
                            </p>
                          )}
                        </div>
                        {historyItem.notes && (
                          <>
                            <Separator className="my-3" />
                            <p className="text-sm text-muted-foreground italic">
                              &ldquo;{historyItem.notes}&rdquo;
                            </p>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Digital Signature Dialog */}
      <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Fingerprint className="h-5 w-5 text-primary" />
              Firma Digitale
            </DialogTitle>
            <DialogDescription>
              Inserisci il tuo PIN per firmare digitalmente il referto. 
              La firma è conforme alle normative vigenti.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Doctor Info */}
            <div className="p-4 rounded-lg bg-muted">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Stethoscope className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">{currentDoctor.name}</p>
                  <p className="text-sm text-muted-foreground">{currentDoctor.specialization}</p>
                </div>
              </div>
            </div>

            {/* PIN Input */}
            <div className="space-y-2">
              <Label htmlFor="signature-pin">PIN Firma Digitale</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="signature-pin"
                  type="password"
                  placeholder="••••••"
                  className="pl-10 text-center text-xl tracking-widest"
                  value={signaturePin}
                  onChange={(e) => setSignaturePin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  data-testid="signature-pin-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Inserisci il PIN a 6 cifre associato al tuo certificato
              </p>
            </div>

            {/* Preview */}
            <div className="p-4 rounded-lg border">
              <p className="text-sm font-medium mb-2">Riepilogo Referto:</p>
              <div className="space-y-1 text-sm text-muted-foreground">
                <p>Paziente: {patient.name}</p>
                <p>Esame: {report.serviceName}</p>
                <p>Data esame: {format(new Date(report.examDate), "d MMMM yyyy", { locale: it })}</p>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowSignDialog(false)}
              disabled={isSigning}
            >
              Annulla
            </Button>
            <Button
              onClick={handleSignReport}
              disabled={isSigning || signaturePin.length < 4}
              className="gap-2"
              data-testid="confirm-signature-btn"
            >
              {isSigning ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                  Firma in corso...
                </>
              ) : (
                <>
                  <PenTool className="h-4 w-4" />
                  Firma Referto
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDTA Activation Dialog */}
      <Dialog open={pdtaDialogOpen} onOpenChange={setPdtaDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pdta-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Attiva PDTA per {patient.name}
            </DialogTitle>
            <DialogDescription>
              Seleziona un Percorso Diagnostico Terapeutico Assistenziale da attivare per il paziente. Verrà generato un piano di prenotazioni automatico.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {pdtaData.filter(p => p.status === "attivo").map(p => {
              const isSel = selectedPdtaId === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedPdtaId(isSel ? null : p.id)}
                  className={cn(
                    "w-full text-left p-3 rounded-lg border transition-all",
                    isSel ? "bg-primary/10 border-primary ring-1 ring-primary" : "bg-card hover:bg-muted/50"
                  )}
                  data-testid={`pdta-pick-${p.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-sm">{p.name}</p>
                    {isSel && <CheckCircle2 className="h-4 w-4 text-primary" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>
                  <div className="flex flex-wrap gap-2 mt-2 text-[11px] text-muted-foreground">
                    <Badge variant="secondary" className="text-[10px]">{p.steps.length} step</Badge>
                    {p.steps.slice(0, 2).map(s => {
                      const ref = s.serviceId ? allServicesData.find(x => x.id === s.serviceId)?.name : allBundlesData.find(x => x.id === s.bundleId)?.name;
                      return <span key={s.id} className="truncate">· {ref}: {describeFrequency(s.frequency)}</span>;
                    })}
                  </div>
                </button>
              );
            })}
            {pdtaData.filter(p => p.status === "attivo").length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6 italic">Nessun PDTA attivo configurato. Chiedere all'amministratore.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPdtaDialogOpen(false); setSelectedPdtaId(null); }}>Annulla</Button>
            <Button
              variant="premium"
              disabled={!selectedPdtaId}
              onClick={() => {
                const pdta = pdtaData.find(p => p.id === selectedPdtaId);
                toast.success(`PDTA "${pdta.name}" attivato`, {
                  description: `${pdta.steps.length} step pianificati per ${patient.name}. Notifiche di promemoria attivate.`
                });
                setPdtaDialogOpen(false);
                setSelectedPdtaId(null);
              }}
              data-testid="pdta-activate-confirm-btn"
            >
              Attiva PDTA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoctorReportDetailPage;
